import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { GalleryPhoto } from "../../lib/api/lidoo.functions";
import { useI18n } from "../../lib/i18n";

// The gallery a beach club page opens with: one large frame and a stack of
// smaller ones, the layout every travel site has trained people to read. It
// degrades honestly — one photo fills the width on its own rather than sitting
// in a grid with three empty holes.
//
// A photo that fails to load is taken out of the set instead of staying a
// broken frame: the next one moves up, and when none is left the caller's
// `fallback` (the drawn plate) takes the gallery's place. Own photos come first
// in `photos`, so this is the same order as the cards: own, Google, plate.
export function PhotoGallery({
  photos: allPhotos,
  name,
  town,
  fallback = null,
}: {
  photos: GalleryPhoto[];
  name: string;
  /** For the alt text of a licensed photo: it shows the beach near here. */
  town?: string;
  fallback?: ReactNode;
}) {
  const { t } = useI18n();
  // A licensed photo is of the surroundings, found by coordinates on Wikimedia
  // Commons or by town name on Openverse. Its alt text says "beach near
  // Vilamoura", never the club's name: a picture from 2 km away is not the club.
  const altFor = (p: GalleryPhoto) =>
    p.source === "licensed" ? t("photo_nearby", { place: town ?? name }) : name;
  const [open, setOpen] = useState<number | null>(null);
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const root = useRef<HTMLDivElement | null>(null);
  const markFailed = useCallback((src: string) => {
    setFailed((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
    setOpen(null);
  }, []);
  const photos = allPhotos.filter((p) => !failed.has(p.src));

  // Images that already failed before hydration never fire onError in React.
  // They report complete with no natural width, so sweep for those once per
  // render of a new set.
  useEffect(() => {
    root.current?.querySelectorAll<HTMLImageElement>("img[data-gallery-src]").forEach((img) => {
      if (img.complete && img.naturalWidth === 0 && img.currentSrc) {
        markFailed(img.dataset.gallerySrc ?? "");
      }
    });
  }, [failed, markFailed]);

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (d: number) => setOpen((i) => (i == null ? i : (i + d + photos.length) % photos.length)),
    [photos.length],
  );

  useEffect(() => {
    if (open == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    // Stop the page behind the lightbox from scrolling under it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close, step]);

  if (photos.length === 0) return <>{fallback}</>;

  const [lead, ...rest] = photos;

  // Two thumbnails beside the hero instead of four.
  //
  // Every Google Places photo that renders is a paid fetch, and Google's terms
  // cap our cache at 30 days, so it is paid again every month. The four
  // thumbnails were two thirds of that bill, and they were being fetched
  // because they sat in the viewport rather than because anyone looked at
  // them: the lightbox is what people actually open, and it loads on click.
  //
  // Two keeps the mosaic reading as a gallery and halves the standing cost.
  // The count on the button still says how many there really are, so nothing
  // is hidden from the visitor.
  const thumbs = rest.slice(0, 2);

  return (
    <>
      <div
        ref={root}
        className={`grid gap-2 overflow-hidden rounded-2xl ${
          thumbs.length > 0 ? "md:grid-cols-[2fr_1fr]" : ""
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen(0)}
          className="group relative block h-[280px] w-full overflow-hidden md:h-[440px]"
        >
          <img
            key={lead.src}
            src={lead.src}
            data-gallery-src={lead.src}
            onError={() => markFailed(lead.src)}
            alt={altFor(lead)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </button>

        {thumbs.length > 0 ? (
          <div className="hidden grid-rows-2 gap-2 md:grid">
            {thumbs.map((p, i) => (
              <button
                key={p.src}
                type="button"
                onClick={() => setOpen(i + 1)}
                className="group relative block h-[108px] overflow-hidden md:h-[216px]"
              >
                <img
                  src={p.src}
                  data-gallery-src={p.src}
                  onError={() => markFailed(p.src)}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                />
                {/* The last visible thumb carries the count, so the rest of the
                    set is discoverable without a separate button. */}
                {i === thumbs.length - 1 && photos.length > thumbs.length + 1 ? (
                  <span className="li-mono absolute inset-0 flex items-center justify-center bg-[#1C2430]/55 text-[13px] text-[#F2EFE9]">
                    +{photos.length - thumbs.length - 1}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="mt-2 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen(0)}
          className="li-mono text-[12px] text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4 hover:text-[#2C4FD6]"
        >
          {t("gallery_all", { n: String(photos.length) })}
        </button>
        <PhotoCredit photo={lead} town={town ?? name} />
      </div>

      {open != null ? (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-[#1C2430]/95 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={name}
        >
          <div className="flex items-center justify-between">
            <p className="li-mono text-[12px] text-[#F2EFE9]/70">
              {open + 1} / {photos.length}
            </p>
            <button
              type="button"
              onClick={close}
              autoFocus
              className="li-mono rounded-full border border-[#F2EFE9]/30 px-4 py-2 text-[12px] text-[#F2EFE9] hover:bg-[#F2EFE9]/10"
            >
              {t("gallery_close")}
            </button>
          </div>

          <div className="flex min-h-0 flex-1 items-center gap-3 py-4">
            {photos.length > 1 ? (
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label={t("gallery_prev")}
                className="shrink-0 rounded-full border border-[#F2EFE9]/30 px-3 py-4 text-[#F2EFE9] hover:bg-[#F2EFE9]/10"
              >
                &lt;
              </button>
            ) : null}
            <img
              // Was rewritten to w=1600, which made the lightbox a third
              // width and therefore a third paid Google fetch of the same
              // photo, on top of the 400 for cards and the 800 here. 800 is
              // already in the cache by the time anyone opens this, so the
              // lightbox is now free.
              src={photos[open].src}
              onError={() => markFailed(photos[open].src)}
              alt={altFor(photos[open])}
              className="mx-auto max-h-full max-w-full rounded-lg object-contain"
            />
            {photos.length > 1 ? (
              <button
                type="button"
                onClick={() => step(1)}
                aria-label={t("gallery_next")}
                className="shrink-0 rounded-full border border-[#F2EFE9]/30 px-3 py-4 text-[#F2EFE9] hover:bg-[#F2EFE9]/10"
              >
                &gt;
              </button>
            ) : null}
          </div>

          <div className="text-center">
            <PhotoCredit photo={photos[open]} town={town ?? name} inverse />
          </div>
        </div>
      ) : null}
    </>
  );
}

// Google's terms require the photographer's name to travel with the picture,
// and so do attribution licences (CC BY, CC BY-SA) on photos we host ourselves:
// for those the line reads "Beach near Vilamoura · Photo: name / CC BY-SA 4.0",
// name linking to the source and the licence to its deed. The "beach near"
// part is there for every licensed photo, credit or not (CC0 has none): it is
// the honest description of what the picture is. A club's own photos, and
// Pexels or Unsplash ones, need no credit and the server sends none, so this
// renders nothing for them.
function PhotoCredit({
  photo,
  town,
  inverse = false,
}: {
  photo: GalleryPhoto;
  town: string;
  inverse?: boolean;
}) {
  const { t } = useI18n();
  const tone = inverse ? "text-[#F2EFE9]/60" : "text-[#5B6472]";
  const link = `li-mono text-[11px] underline underline-offset-2 ${tone}`;

  if (photo.source === "licensed") {
    const nearby = (
      <span title={t("photo_nearby_note")}>{t("photo_nearby", { place: town })}</span>
    );
    if (!photo.attribution) return <span className={`li-mono text-[11px] ${tone}`}>{nearby}</span>;
    const label = t("photo_credit", { name: photo.attribution });
    return (
      <span className={`li-mono text-[11px] ${tone}`}>
        {nearby}
        {" · "}
        {photo.attributionUri ? (
          <a href={photo.attributionUri} target="_blank" rel="noopener noreferrer nofollow" className={link}>
            {label}
          </a>
        ) : (
          label
        )}
        {photo.license ? (
          <>
            {" / "}
            {photo.licenseUri ? (
              <a href={photo.licenseUri} target="_blank" rel="license noopener noreferrer nofollow" className={link}>
                {photo.license}
              </a>
            ) : (
              photo.license
            )}
          </>
        ) : null}
      </span>
    );
  }

  if (photo.source !== "google") return null;
  const label = photo.attribution
    ? t("photo_by", { name: photo.attribution })
    : t("photo_google");
  return photo.attributionUri ? (
    <a
      href={photo.attributionUri}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className={link}
    >
      {label}
    </a>
  ) : (
    <span className={`li-mono text-[11px] ${tone}`}>{label}</span>
  );
}
